// Agenda das etapas — fonte única dos offsets.
//
// Antes esses números viviam em dois lugares (ServiceModal montava as
// sugestões, DashboardTab derivava os eventos do calendário) e podiam divergir
// sem ninguém perceber. O calendário só é "condizente" com o cadastro se os
// dois lerem daqui.
//
// Cada etapa declara DE ONDE pende (`from`) e a que distância (`offset`). Era
// uma corrente — cada etapa em relação à anterior — e virou árvore quando as
// DGs passaram a contar da FIV: numa corrente, mexer na TE arrastava a DG
// junto, e não é isso que acontece. A idade gestacional começa na fecundação,
// então DG30 e DG60 penduram na FIV e ignoram a data da TE.
//
// A FIV é no dia seguinte à OPU e a TE SETE dias depois dela — ou seja, OPU+8.
// Antes a TE era OPU+7, e ao mover a FIV para OPU+1 eu a mantive no mesmo dia;
// o laboratório corrigiu: o prazo é contado da FIV, e a TE anda junto com ela.
//
// D0 DEIXOU DE SER A OPU e passou a ser o implante do protocolo. A OPU pende
// dele porque é ela que é marcada a partir da retirada, e não o contrário: o
// implante já entrou na vaca, e adiar a aspiração não muda o dia em que isso
// aconteceu. A régua garante isso — remarcar a OPU não cascateia para trás,
// porque `distanciaNaArvore` não acha caminho subindo e a régua daria -8, que
// o `naRegua >= 0` descarta. Enquanto o protocolo NÃO foi lançado, porém, a
// data dele é plano e acompanha a OPU: isso é `agendaDaOpu`, não a cascata.
//
// O +8 sai do protocolo mais usado: implante em D0, retirada em D7, aspiração
// em D8. Serviço que não contrata protocolo continua exatamente como era — a
// etapa não contratada nunca entra na conta.
//
// NUNCA duas raízes: com `from: null` no protocolo E na OPU, `dayFromRoot`
// devolveria 0 para as duas, `daysBetweenStages` daria 0 entre elas, e a
// agenda inteira desabaria sem erro nenhum.
//
// `notesField` é a observação que acompanha a data agendada no cadastro. A FIV
// não tem: nunca ganhou coluna no banco, e o cadastro tinha um campo para ela
// mesmo assim — quem escrevia ali derrubava o salvar com "coluna não
// encontrada". Etapa sem `notesField` não mostra observação, e pronto.
export const SCHEDULE_CHAIN = [
  { id: 'protocolo', label: 'Protocolo', field: 'protocolo_scheduled_date',  actualField: 'protocolo_date',      doneField: 'protocolo_done',     notesField: 'scheduled_protocolo_notes',     from: null,        offset: 0,  requires: 'protocolo', tolerancia: 7 },
  { id: 'opu',  label: 'OPU',  field: 'opu_scheduled_date',           actualField: 'opu_date',            doneField: 'opu_done',           notesField: 'scheduled_opu_notes',           from: 'protocolo', offset: 8,  requires: 'opu', tolerancia: 1 },
  { id: 'fiv',  label: 'FIV',  field: 'lab_scheduled_date',           actualField: 'lab_date',            doneField: 'lab_done',           notesField: null,                            from: 'opu', offset: 1,  requires: 'fiv', tolerancia: 7 },
  { id: 'te',   label: 'TE',   field: 'te_scheduled_date',            actualField: 'te_date',             doneField: 'te_done',            notesField: 'scheduled_te_notes',            from: 'fiv', offset: 7,  requires: 'te', tolerancia: 7 },
  { id: 'dg30', label: 'DG30', field: 'pregnancy_30d_scheduled_date', actualField: 'pregnancy_30d_date',  doneField: 'pregnancy_30d_done', notesField: 'scheduled_pregnancy_30d_notes', from: 'fiv', offset: 30, requires: 'pregnancy_check', tolerancia: 7 },
  { id: 'dg60', label: 'DG60', field: 'pregnancy_60d_scheduled_date', actualField: 'pregnancy_60d_date',  doneField: 'pregnancy_60d_done', notesField: 'scheduled_pregnancy_60d_notes', from: 'fiv', offset: 60, requires: 'pregnancy_check', tolerancia: 7 },
];

// Eventos que o calendário deriva de outra etapa em vez de ter data própria.
// `from` aponta para um id da corrente acima.
export const DERIVED_EVENTS = [
  // A retirada não tem data própria: sai do cronograma hormonal. No calendário
  // ela importa porque é um dia em que alguém tem que ir à fazenda.
  { id: 'retirada', label: 'Retirada', from: 'protocolo', offset: 7, requires: 'protocolo' },
  { id: 'cleavage', label: 'Clivagem', from: 'opu', offset: 2, requires: 'fiv' },
  { id: 'civ',      label: 'CIV',      from: 'fiv', offset: 3, requires: 'fiv' },
];

export const SCHEDULE_BY_ID = Object.fromEntries(SCHEDULE_CHAIN.map(s => [s.id, s]));

// ---------------------------------------------------------------------------

/** 'YYYY-MM-DD' + n dias, sem passar por fuso. */
export const addDays = (dateStr, days) => {
  if (!dateStr) return null;
  const [y, m, d] = String(dateStr).slice(0, 10).split('-').map(Number);
  if (!y || !m || !d) return null;
  // Date.UTC evita o off-by-one que `new Date('2026-08-17')` causa em fuso
  // negativo — o app roda em BRT e a data voltava um dia.
  const t = new Date(Date.UTC(y, m - 1, d + days));
  return t.toISOString().slice(0, 10);
};

/** Dia da etapa na régua canônica, contado da raiz (OPU = D0). */
const dayFromRoot = (stageId) => {
  let dias = 0;
  let atual = SCHEDULE_BY_ID[stageId];
  while (atual?.from) {
    dias += atual.offset;
    atual = SCHEDULE_BY_ID[atual.from];
  }
  return dias;
};

/**
 * Distância subindo a árvore de `toId` até `fromId`.
 * `null` quando `fromId` não está no caminho — isto é, quando mexer em
 * `fromId` não deve mover `toId`.
 */
const distanciaNaArvore = (fromId, toId) => {
  let dias = 0;
  let atual = SCHEDULE_BY_ID[toId];
  while (atual) {
    if (atual.id === fromId) return dias;
    if (!atual.from) return null;
    dias += atual.offset;
    atual = SCHEDULE_BY_ID[atual.from];
  }
  return null;
};

/** A etapa de que esta pende está contratada? */
const ancoraContratada = (stage, offered) => {
  const pai = SCHEDULE_BY_ID[stage.from];
  return !!pai && offered.includes(pai.requires);
};

/**
 * Distância em dias entre duas etapas na régua canônica.
 *
 * Serve a quem quer só o prazo (o alerta de DG60 atrasada, por exemplo), e por
 * isso usa a régua, não a árvore: entre duas etapas quaisquer sempre há uma
 * diferença de dias, mesmo quando uma não pende da outra.
 */
export const daysBetweenStages = (fromId, toId) => {
  if (!SCHEDULE_BY_ID[fromId] || !SCHEDULE_BY_ID[toId]) return 0;
  return dayFromRoot(toId) - dayFromRoot(fromId);
};

/**
 * Datas a partir de `anchorId`, ancorando ele em `anchorDate`.
 *
 * Uma etapa só se move se PENDE da âncora — é isso que mantém a DG parada
 * quando se remarca a TE. A exceção é o serviço que não contratou a etapa de
 * que ela pende (TE avulsa, com embrião vindo de fora): sem FIV no serviço, a
 * DG não tem onde se apoiar, e aí cai na distância da régua canônica. Sem essa
 * saída ela ficaria simplesmente sem data.
 */
export const cascadeFrom = (anchorId, anchorDate, offered = []) => {
  if (!SCHEDULE_BY_ID[anchorId] || !anchorDate) return {};

  const dates = {};
  SCHEDULE_CHAIN.forEach((stage) => {
    if (!offered.includes(stage.requires)) return;

    const naArvore = distanciaNaArvore(anchorId, stage.id);
    if (naArvore !== null) {
      dates[stage.field] = addDays(anchorDate, naArvore);
      return;
    }

    // Fora do alcance da âncora: só entra se não houver quem a ancore.
    if (ancoraContratada(stage, offered)) return;

    const naRegua = daysBetweenStages(anchorId, stage.id);
    if (naRegua >= 0) dates[stage.field] = addDays(anchorDate, naRegua);
  });
  return dates;
};

/**
 * Datas a partir da OPU, INCLUSIVE o protocolo, que fica para trás.
 *
 * A agenda pensa a partir do implante (D0), mas a equipe marca pela OPU: é o
 * dia de levar a equipe à fazenda, e o resto se encaixa em volta dele. Então
 * marcar a OPU tem de devolver também o início do protocolo, oito dias antes,
 * e não só o que vem depois. Errar essa conta à mão é perder o serviço.
 *
 * O "remarcar a OPU não volta no tempo" continua valendo, mas só para o que
 * JÁ ACONTECEU: com o protocolo lançado, o implante está na vaca e a data dele
 * é fato. Antes disso ela é só plano, e o plano anda junto com a OPU.
 * `cascadeFrom` sozinho nunca devolve o protocolo a partir da OPU — a régua
 * daria -8 e ele descarta —, por isso a conta para trás mora aqui.
 */
export const agendaDaOpu = (opuDate, offered = [], { protocoloFeito = false } = {}) => {
  if (!opuDate) return {};
  const datas = cascadeFrom('opu', String(opuDate).slice(0, 10), offered);
  if (offered.includes('protocolo') && !protocoloFeito) {
    datas[SCHEDULE_BY_ID.protocolo.field] = addDays(opuDate, -SCHEDULE_BY_ID.opu.offset);
  }
  return datas;
};

/**
 * Corrente inteira a partir da data do serviço. A primeira etapa contratada
 * cai no próprio dia do serviço — data do serviço e da OPU podem coincidir.
 */
export const scheduleFromServiceDate = (serviceDate, offered = []) => {
  const first = SCHEDULE_CHAIN.find(s => offered.includes(s.requires));
  if (!first || !serviceDate) return {};
  return cascadeFrom(first.id, String(serviceDate).slice(0, 10), offered);
};

/**
 * Onde a etapa cai na agenda: a data real ganha da agendada, porque depois de
 * executada é ela que descreve o que aconteceu. Antes do fato, vale o plano.
 */
export const calendarDateOf = (service, stageId) => {
  const stage = SCHEDULE_BY_ID[stageId];
  if (!stage || !service) return null;
  return service[stage.actualField] || service[stage.field] || null;
};

/**
 * Datas da etapa herdada, copiadas da origem.
 *
 * Ao casar a origem, o serviço passa a saber QUANDO aquela etapa aconteceu —
 * mesmo sem tê-la executado. Não é enfeite: `lab_date` é o que ancora o prazo
 * da DG60 e as datas previstas por receptora. Sem ela, o serviço linkado caía
 * na reserva pela TE e contava a gestação do dia errado.
 *
 * Copia a agendada e, quando a origem já executou, também a real — porque aí a
 * data é fato, não plano.
 */
export const datasHerdadas = (origem, stageId) => {
  const etapa = SCHEDULE_BY_ID[stageId];
  if (!etapa || !origem) return {};

  const agendada = origem[etapa.field] || origem[etapa.actualField];
  const real = origem[etapa.actualField];

  return {
    ...(agendada ? { [etapa.field]: agendada } : {}),
    ...(real ? { [etapa.actualField]: real } : {}),
  };
};

/**
 * Em que etapa abrir o serviço: a que vem DEPOIS da última concluída.
 *
 * Abrir sempre na primeira etapa contratada era abrir numa OPU concluída há
 * três semanas. Quem abre um serviço vai lançar o que falta, e tinha de achar
 * a aba certa toda vez.
 *
 * É a última CONCLUÍDA, não a primeira pendente. Etapa marcada fora de ordem —
 * um DG lançado antes de a TE ser fechada — não devia puxar de volta para
 * trás: o serviço andou, e o lugar de continuar é na frente.
 *
 * Só olha etapa contratada. E quando tudo está concluído, abre na última: não
 * há "depois", e a última é onde se confere o que foi feito.
 */
export const etapaParaAbrir = (service) => {
  const oferecidas = service?.services_offered || [];
  const contratadas = SCHEDULE_CHAIN.filter(s => oferecidas.includes(s.requires));
  if (contratadas.length === 0) return null;

  let ultimaConcluida = -1;
  contratadas.forEach((s, i) => { if (service?.[s.doneField]) ultimaConcluida = i; });

  const proxima = contratadas[ultimaConcluida + 1];
  return (proxima || contratadas[contratadas.length - 1]).id;
};

/**
 * As etapas que o serviço contratou, na ordem, com o estado de cada uma.
 *
 * Uma entrada por TIPO contratado, não por etapa da corrente: DG30 e DG60 são
 * duas etapas do mesmo `pregnancy_check`, e "concluiu a conferência" sempre
 * quis dizer que o DG60 fechou — o de 30 dias é passagem, não fim. Por isso a
 * última etapa de cada tipo é a que vale.
 *
 * Existe porque esse mapa estava escrito à mão em `getServiceStatus` e em
 * `checkAllContractedStagesDone`, os dois com as mesmas quatro etapas fixas.
 * Etapa nova na corrente entrava em lugar nenhum, e o serviço ficava
 * "Finalizado" com etapa em aberto — que em `pacote_completo` é dinheiro
 * faturado antes da hora.
 */
export const etapasContratadas = (service) => {
  const oferecidas = service?.services_offered || [];
  return SCHEDULE_CHAIN
    .filter(etapa => oferecidas.includes(etapa.requires))
    .map(etapa => ({ ...etapa, done: !!service?.[etapa.doneField] }));
};

/**
 * Todas as contratadas concluídas? Falso quando não há etapa contratada.
 *
 * Olha a ÚLTIMA etapa de cada tipo, não todas: DG30 e DG60 são duas etapas do
 * mesmo `pregnancy_check`, e "concluiu a conferência" sempre quis dizer que o
 * DG60 fechou — o de 30 dias é passagem, não fim. Exigir os dois mudaria
 * silenciosamente quando um `pacote_completo` vira faturável.
 */
export const todasAsEtapasConcluidas = (service) => {
  const porTipo = new Map();
  etapasContratadas(service).forEach(e => porTipo.set(e.requires, e));
  const ultimas = [...porTipo.values()];
  return ultimas.length > 0 && ultimas.every(e => e.done);
};

/** A primeira etapa contratada ainda em aberto, ou `null` se acabou tudo. */
export const etapaPendente = (service) =>
  etapasContratadas(service).find(e => !e.done) || null;

/**
 * Etapas que um serviço pode HERDAR de outro.
 *
 * A regra é "o que vem antes da primeira etapa que ele mesmo contratou": um
 * serviço só de TE herda a OPU ou a FIV de outro; quem contrata a própria OPU
 * não herda nada, porque a corrente começa nele.
 *
 * O PROTOCOLO FICA DE FORA DA CONTA, e é isto que o nome desta função existe
 * para deixar explícito. Ele é a raiz da corrente de datas (D0 = implante),
 * mas não pertence à corrente de PRODUÇÃO: sincronizar receptora prepara quem
 * recebe, não produz embrião nenhum.
 *
 * Enquanto ele entrava na conta, marcar protocolo colocava a "primeira etapa
 * contratada" no índice 0 e a herança sumia inteira — justamente no caso mais
 * comum de herdar, que é o serviço de protocolo + TE recebendo embrião
 * produzido noutro serviço.
 */
const CORRENTE_DE_PRODUCAO = SCHEDULE_CHAIN.filter(e => e.id !== 'protocolo');

export const etapasQuePodeHerdar = (oferecidos = []) => {
  const primeira = CORRENTE_DE_PRODUCAO.findIndex(e => oferecidos.includes(e.requires));
  if (primeira <= 0) return [];
  // Só OPU e FIV são herdáveis: são as que produzem algo que outro serviço
  // consome. Herdar uma TE ou um DG não significaria nada.
  return CORRENTE_DE_PRODUCAO
    .slice(0, primeira)
    .filter(e => e.id === 'opu' || e.id === 'fiv');
};

/**
 * Etapas que já deviam ter acontecido e não foram lançadas.
 *
 * "Rolou e ninguém lançou" é diferente de "está atrasada": a OPU pode ter
 * acontecido no dia certo e a ficha ter ficado na caminhonete. O que este
 * alerta persegue é o lançamento que falta, não o trabalho que falta — e os
 * dois se parecem na tela até alguém cobrar o cliente por algo que não foi
 * feito, ou deixar de cobrar por algo que foi.
 *
 * A data olhada é a AGENDADA. A real só existe depois do lançamento, então
 * usar `calendarDateOf` aqui seria perguntar pela data de um fato que, por
 * definição deste alerta, ainda não foi registrado.
 *
 * A TOLERÂNCIA É DE CADA ETAPA, e não uma só para todas.
 *
 * Sete dias serve para a papelada que volta do campo: a TE aconteceu, a ficha
 * ficou na caminhonete, e cobrar no dia seguinte seria cobrar de quem está
 * dirigindo. A OPU é outra coisa. Ela é o começo da corrente: a FIV é no dia
 * SEGUINTE, e os oócitos aspirados são o número que a FIV consome. Uma OPU que
 * passou e não foi lançada significa uma de duas coisas, e as duas pedem
 * alguém hoje — ou ela aconteceu e a FIV está sendo feita sobre número
 * nenhum, ou ela não aconteceu e a agenda inteira abaixo dela está errada.
 * Esperar uma semana para dizer isso é esperar a semana em que o serviço
 * andou torto.
 *
 * `tolerancia: 1` é "cobra no dia seguinte": no próprio dia agendado a equipe
 * ainda está na fazenda, e um alerta ali acusaria atraso de trabalho em curso.
 *
 * Etapa HERDADA não entra, e não precisa de regra nova para isso: quem herda a
 * OPU de outro serviço não a contrata, então `etapasContratadas` já a deixa de
 * fora. A data herdada existe no serviço — é ela que ancora a corrente —, mas o
 * lançamento é do serviço de origem, e cobrar aqui mandaria a pessoa lançar uma
 * coleta que não foi dela.
 *
 * `dias` corridos, não úteis: o prazo é sobre a memória de quem lançou, e ela
 * não tira fim de semana. Passar `dias` força um valor único para todas as
 * etapas, o que serve para teste e para quem quer uma varredura mais larga.
 */
export const DIAS_DE_TOLERANCIA_PADRAO = 7;

export const etapasSemLancamento = (service, hoje = new Date(), dias = null) => {
  const corteDe = (t) => {
    const limite = new Date(hoje);
    limite.setDate(limite.getDate() - t);
    return limite.toISOString().slice(0, 10);
  };

  return etapasContratadas(service)
    .filter(etapa => !etapa.done)
    .map(etapa => ({ etapa, agendada: service?.[etapa.field] || null }))
    .filter(x => x.agendada && x.agendada <= corteDe(
      dias === null
        ? (x.etapa.tolerancia ?? DIAS_DE_TOLERANCIA_PADRAO)
        : dias))
    .map(x => ({
      ...x.etapa,
      agendada: x.agendada,
      diasSemLancamento: Math.floor(
        (new Date(`${hoje.toISOString().slice(0, 10)}T00:00:00Z`)
          - new Date(`${x.agendada}T00:00:00Z`)) / 86400000),
    }))
    .sort((a, b) => b.diasSemLancamento - a.diasSemLancamento);
};

/** O serviço tem alguma etapa esperando lançamento? */
export const esperandoLancamento = (service, hoje = new Date(), dias = null) =>
  etapasSemLancamento(service, hoje, dias).length > 0;
